import { Layout } from 'react-grid-layout';
import { LEGACY_WIDGET_MAPPING } from '@/features/dashboard/constants';
import { DashboardLayouts, Widget, GridConfig } from './dashboardLayoutTypes';

/* ============================================================================
 * DASHBOARD LAYOUT DATA TRANSFORMERS
 * Converts between frontend layout format and backend widget format
 * ============================================================================ */

interface WidgetSaveDefinition {
  id: string;
  title?: string;
  deletable?: boolean;
  defaultSize?: { w: number; h: number };
  type?: string;
}

const BREAKPOINT_PRIORITY: Array<keyof DashboardLayouts> = ['lg', 'md', 'sm', 'xs', 'xxs'];

const normalizeWidgetId = (widgetId: string): string => LEGACY_WIDGET_MAPPING[widgetId] || widgetId;

const getLayoutItemForWidget = (layouts: DashboardLayouts, widgetId: string): Layout | undefined => {
  const normalizedWidgetId = normalizeWidgetId(widgetId);

  for (const breakpoint of BREAKPOINT_PRIORITY) {
    const layoutItem = layouts[breakpoint]?.find(item => normalizeWidgetId(item.i) === normalizedWidgetId);
    if (layoutItem) {
      return layoutItem;
    }
  }

  return undefined;
};

const getLayoutConstraints = (layoutItem: Layout) => ({
  ...(layoutItem.minW !== undefined ? { minW: layoutItem.minW } : {}),
  ...(layoutItem.maxW !== undefined ? { maxW: layoutItem.maxW } : {}),
  ...(layoutItem.minH !== undefined ? { minH: layoutItem.minH } : {}),
  ...(layoutItem.maxH !== undefined ? { maxH: layoutItem.maxH } : {}),
  ...(layoutItem.static !== undefined ? { static: layoutItem.static } : {}),
  ...(layoutItem.isDraggable !== undefined ? { isDraggable: layoutItem.isDraggable } : {}),
  ...(layoutItem.isResizable !== undefined ? { isResizable: layoutItem.isResizable } : {}),
});

export function buildWidgetDataForSave(
  layouts: DashboardLayouts,
  widgetDefinitions: WidgetSaveDefinition[],
  activeWidgetIds: Iterable<string>,
  existingWidgets?: Widget[] | null
): Widget[] {
  const activeIds = new Set(Array.from(activeWidgetIds));
  const existingWidgetsMap = new Map(
    (existingWidgets || []).map(widget => [normalizeWidgetId(widget.id), widget])
  );

  return widgetDefinitions.map(widgetDefinition => {
    const layoutItem = getLayoutItemForWidget(layouts, widgetDefinition.id);
    const existingWidget = existingWidgetsMap.get(widgetDefinition.id);
    const fallbackPosition = existingWidget?.position
      ? {
          x: existingWidget.position.x ?? 0,
          y: existingWidget.position.y ?? 0,
          w: existingWidget.position.w ?? widgetDefinition.defaultSize?.w ?? 4,
          h: existingWidget.position.h ?? widgetDefinition.defaultSize?.h ?? 4
        }
      : {
          x: 0,
          y: 0,
          w: widgetDefinition.defaultSize?.w ?? 4,
          h: widgetDefinition.defaultSize?.h ?? 4
        };

    return {
      id: widgetDefinition.id,
      type: existingWidget?.type || widgetDefinition.type || 'dashboard_widget',
      position: layoutItem
        ? {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h
          }
        : fallbackPosition,
      config: {
        ...existingWidget?.config,
        ...(widgetDefinition.title !== undefined ? { title: widgetDefinition.title } : {}),
        ...(widgetDefinition.deletable !== undefined ? { deletable: widgetDefinition.deletable } : {}),
        ...(layoutItem ? getLayoutConstraints(layoutItem) : {})
      },
      is_visible: activeIds.has(widgetDefinition.id)
    };
  });
}

/**
 * Convert react-grid-layout format to backend widget format
 */
export function convertLayoutsToWidgets(layouts: DashboardLayouts, widgetData?: Widget[]): Widget[] {
  // CRITICAL FIX: Process ALL widgets from widgetData when available, not just those in layouts
  // This ensures invisible widgets are sent to backend with is_visible=false
  if (widgetData && widgetData.length > 0) {
    // Processing ALL widgets from widgetData, including invisible ones
    
    const widgets = widgetData.map(widget => {
      // Find layout position if widget is visible and in layout
      const layoutItem = getLayoutItemForWidget(layouts, widget.id);
      
      return {
        id: widget.id,
        type: widget.type || 'dashboard_widget',
        position: layoutItem ? {
          x: layoutItem.x,
          y: layoutItem.y,
          w: layoutItem.w,
          h: layoutItem.h
        } : widget.position ? {
          x: widget.position.x ?? 0,
          y: widget.position.y ?? 0,
          w: widget.position.w ?? widget.defaultSize?.w ?? 4,
          h: widget.position.h ?? widget.defaultSize?.h ?? 4
        } : { x: 0, y: 0, w: widget.defaultSize?.w ?? 4, h: widget.defaultSize?.h ?? 4 }, // Default position for hidden widgets
        config: {
          ...widget.config,
          ...(layoutItem ? getLayoutConstraints(layoutItem) : {})
        },
        is_visible: widget.is_visible !== undefined
          ? widget.is_visible
          : widget.isVisible !== undefined
            ? widget.isVisible
            : true
      };
    });
    
    // Converted widgets with visibility states
    return widgets;
  }
  
  // Fallback to original logic when no widgetData is provided
  const lgLayouts = layouts.lg || [];
  // log.debug('DashboardLayoutTransformers', LOG_MESSAGES.CONVERSION_START, { lgLayouts, widgetData });
  
  const widgets = lgLayouts.map(item => {
    // Try to find widget metadata from widgetData if provided
    const widget = widgetData?.find(w => normalizeWidgetId(w.id) === item.i);
    
    return {
      id: item.i, // Backend expects 'id' not 'i'
      type: widget?.type || 'dashboard_widget', // Use actual widget type if available
      position: {
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      },
      config: {
        ...widget?.config, // Include widget-specific config
        ...(item.minW !== undefined ? { minW: item.minW } : {}),
        ...(item.maxW !== undefined ? { maxW: item.maxW } : {}),
        ...(item.minH !== undefined ? { minH: item.minH } : {}),
        ...(item.maxH !== undefined ? { maxH: item.maxH } : {}),
        static: item.static || false,
        isDraggable: item.isDraggable !== false,
        isResizable: item.isResizable !== false
      },
      is_visible: widget?.is_visible !== undefined ? widget.is_visible : 
                  widget?.isVisible !== undefined ? widget.isVisible : true // Handle both snake_case and camelCase from backend
    };
  });
  
  // log.debug('DashboardLayoutTransformers', LOG_MESSAGES.CONVERSION_SUCCESS, widgets);
  return widgets;
}

/**
 * Convert backend widget format to react-grid-layout format
 */
export function convertWidgetsToLayouts(widgets: Widget[], gridConfig?: GridConfig): DashboardLayouts {
  // log.debug('DashboardLayoutTransformers', LOG_MESSAGES.CONVERSION_START, { widgets, gridConfig });
  
  // If gridConfig has breakpoints, use them directly
  if (gridConfig?.breakpoints) {
    // log.debug('DashboardLayoutTransformers', LOG_MESSAGES.USING_BREAKPOINTS);
    const breakpoints = gridConfig.breakpoints;
    const mapLayoutItems = (items: Layout[]) => {
      if (!Array.isArray(items)) return [];
      return items.map(item => ({
        ...item,
        i: LEGACY_WIDGET_MAPPING[item.i] || item.i // Map legacy IDs
      })).filter((item, index, self) => 
        // Remove duplicates based on widget ID
        self.findIndex(i => i.i === item.i) === index
      );
    };
    
    return {
      lg: mapLayoutItems(breakpoints.lg || []),
      md: mapLayoutItems(breakpoints.md || []),
      sm: mapLayoutItems(breakpoints.sm || []),
      xs: mapLayoutItems(breakpoints.xs || []),
      xxs: mapLayoutItems(breakpoints.xxs || [])
    };
  }
  
  // If no breakpoints in config, create responsive layouts from widgets
  // log.debug('DashboardLayoutTransformers', LOG_MESSAGES.CREATING_LAYOUTS);
  
  if (!widgets || widgets.length === 0) {
    // log.debug('DashboardLayoutTransformers', LOG_MESSAGES.NO_WIDGETS);
    return {
      lg: [],
      md: [],
      sm: [],
      xs: [],
      xxs: []
    };
  }
  
  const baseLayout: Layout[] = widgets.map(widget => {
    const widgetId = widget.id || widget.i || ''; // Backend sends 'id', frontend expects 'i'
    const mappedId = LEGACY_WIDGET_MAPPING[widgetId] || widgetId; // Apply legacy mapping
    
    const layoutItem: Layout = {
      i: mappedId,
      x: widget.position?.x ?? widget.x ?? 0,
      y: widget.position?.y ?? widget.y ?? 0,
      w: widget.position?.w ?? widget.w ?? 4,
      h: widget.position?.h ?? widget.h ?? 4,
      minW: widget.config?.minW || widget.minW,
      maxW: widget.config?.maxW || widget.maxW,
      minH: widget.config?.minH || widget.minH,
      maxH: widget.config?.maxH || widget.maxH,
      static: widget.config?.static || widget.static || false,
      isDraggable: widget.config?.isDraggable !== false && widget.isDraggable !== false,
      isResizable: widget.config?.isResizable !== false && widget.isResizable !== false
    };
    // log.debug('DashboardLayoutTransformers', '📦 Widget to layout item:', { widget, layoutItem });
    return layoutItem;
  }).filter((item, index, self) => 
    // Remove duplicates based on widget ID
    self.findIndex(i => i.i === item.i) === index
  );

  // Create responsive layouts for different breakpoints
  return {
    lg: baseLayout,
    md: baseLayout.map(item => ({ ...item, w: Math.min(item.w, 8) })),
    sm: baseLayout.map(item => ({ ...item, w: Math.min(item.w, 6) })),
    xs: baseLayout.map(item => ({ ...item, w: Math.min(item.w, 4) })),
    xxs: baseLayout.map(item => ({ ...item, w: Math.min(item.w, 3) }))
  };
}

/**
 * Create grid config from layouts
 */
export function createGridConfig(layouts: DashboardLayouts): GridConfig {
  return {
    breakpoints: layouts
  };
}
